import { useState, useEffect } from 'react'
import api from '../services/api'

interface Dissidio {
  codigo: string
  descricao: string
}

interface Colaborador {
  motcod: string
  empresa_codigo: number | null
  empresa_nome: string | null
  motnome: string
  categoria_codigo: string | null
  categoria_descricao: string | null
  data_admissao: string | null
  status: string
  jornada: string | null
  jornada_mensal: number | null
  dissidio_codigo: string | null
  dissidio_descricao: string | null
}

function formatarData(iso: string | null) {
  if (!iso) return '—'
  const [ano, mes, dia] = iso.slice(0, 10).split('-')
  return `${dia}/${mes}/${ano}`
}

function numeroParaHM(v: string | null): string {
  if (v === null || v === undefined) return '—'
  const num = Number(v)
  const horas = Math.floor(num)
  const minutos = Math.round((num - horas) * 100)
  return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}`
}

function SelectDissidio({ colaborador, dissidios, onSalvo }: {
  colaborador: Colaborador
  dissidios: Dissidio[]
  onSalvo: (motcod: string, dissidio_codigo: string | null, dissidio_descricao: string | null) => void
}) {
  const [salvando, setSalvando] = useState(false)

  async function alterar(novoCodigo: string) {
    setSalvando(true)
    try {
      await api.put(`/cartao-ponto/colaboradores-cadastro/${colaborador.motcod}`, {
        dissidio_codigo: novoCodigo || null,
      })
      const dissidio = dissidios.find(d => d.codigo === novoCodigo)
      onSalvo(colaborador.motcod, novoCodigo || null, dissidio?.descricao ?? null)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <select
      value={colaborador.dissidio_codigo ?? ''}
      disabled={salvando}
      style={{ fontSize: '0.8rem', maxWidth: 260 }}
      onChange={e => alterar(e.target.value)}
    >
      <option value="">— não cadastrado —</option>
      {dissidios.map(d => (
        <option key={d.codigo} value={d.codigo}>{d.codigo} - {d.descricao}</option>
      ))}
    </select>
  )
}

export default function CadastroColaboradores() {
  const [status, setStatus] = useState<'ativos' | 'inativos' | 'todos'>('ativos')
  const [busca, setBusca] = useState('')
  const [lista, setLista] = useState<Colaborador[]>([])
  const [dissidios, setDissidios] = useState<Dissidio[]>([])
  const [carregando, setCarregando] = useState(false)

  async function carregar() {
    setCarregando(true)
    try {
      const r = await api.get('/cartao-ponto/colaboradores-cadastro', { params: { status, busca: busca.trim() || undefined } })
      setLista(r.data)
    } finally {
      setCarregando(false)
    }
  }

  useEffect(() => {
    api.get('/ficha-ponto/dissidios').then(r => setDissidios(r.data))
  }, [])

  useEffect(() => { carregar() }, [status])

  function aoSalvarDissidio(motcod: string, dissidio_codigo: string | null, dissidio_descricao: string | null) {
    setLista(l => l.map(c => c.motcod === motcod ? { ...c, dissidio_codigo, dissidio_descricao } : c))
  }

  const semDissidioCount = lista.filter(c => !c.dissidio_codigo).length

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '0.6rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Cadastro de Colaboradores</h2>
        <span style={{ color: '#9ca3af', fontSize: '0.75rem', fontFamily: 'monospace' }} title="Tabela frota.cadmot">CADMOT</span>
      </div>
      <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 0, marginBottom: '1rem' }}>
        Listagem com os dados do colaborador relevantes pra ficha ponto. O cadastro completo continua sendo feito em outra tela —
        aqui o único campo editável é o <b>dissídio</b>, que passa a valer pra abertura de fichas (individual ou em lote) desse colaborador.
      </p>

      <div className="card" style={{ marginBottom: '1.2rem' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div className="field" style={{ width: 240 }}>
            <label>Código ou nome</label>
            <input
              type="text"
              value={busca}
              placeholder="Buscar..."
              onChange={e => setBusca(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') carregar() }}
            />
          </div>
          <div className="field" style={{ width: 180 }}>
            <label>Situação</label>
            <select value={status} onChange={e => setStatus(e.target.value as typeof status)}>
              <option value="ativos">Ativos</option>
              <option value="inativos">Inativos</option>
              <option value="todos">Todos</option>
            </select>
          </div>
          <button className="btn btn-outline" onClick={carregar}>Filtrar</button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            {lista.length} colaborador(es){lista.length === 500 && ' (limite de 500 — refine a busca se faltar alguém)'}
            {semDissidioCount > 0 && <span style={{ color: '#b45309' }}> · {semDissidioCount} sem dissídio cadastrado</span>}
          </span>
        </div>
      </div>

      <div className="card">
        {carregando && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Carregando...</p>}
        {!carregando && lista.length === 0 && (
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Nenhum colaborador encontrado.</p>
        )}
        {!carregando && lista.length > 0 && (
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px' }}>Código</th>
                  <th style={{ padding: '6px 8px' }}>Empresa</th>
                  <th style={{ padding: '6px 8px' }}>Nome</th>
                  <th style={{ padding: '6px 8px' }}>Categoria</th>
                  <th style={{ padding: '6px 8px' }}>Admissão</th>
                  <th style={{ padding: '6px 8px' }}>Situação</th>
                  <th style={{ padding: '6px 8px' }}>Jornada</th>
                  <th style={{ padding: '6px 8px' }}>Jornada Mensal</th>
                  <th style={{ padding: '6px 8px' }}>Dissídio</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((c, i) => (
                  <tr
                    key={c.motcod}
                    style={{ borderBottom: '1px solid #f1f5f9', background: !c.dissidio_codigo ? '#fffbeb' : (i % 2 === 0 ? '#ffffff' : '#f9fafb') }}
                  >
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{c.motcod}</td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{c.empresa_codigo} - {c.empresa_nome}</td>
                    <td style={{ padding: '4px 8px' }}>{c.motnome}</td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }} title={c.categoria_codigo ?? ''}>{c.categoria_descricao || '—'}</td>
                    <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{formatarData(c.data_admissao)}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <span className={c.status === 'A' ? 'badge badge-success' : 'badge badge-default'}>
                        {c.status === 'A' ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>{numeroParaHM(c.jornada)}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'center' }}>{c.jornada_mensal ?? '—'}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <SelectDissidio colaborador={c} dissidios={dissidios} onSalvo={aoSalvarDissidio} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
